import { css } from '@emotion/react';
import React from 'react';

import Loading from '$/components/Loading';
import { usePermissionModal } from '$/components/Permission';

import { BillingCycle, useAddOnsQuery } from '$/GraphQL/schema/schema';
import {
  MeFragment,
  PriceTierFragment,
  ProductForAdminFragment,
  ProductFragment,
  ProductType,
  SubscriptionSubstatus,
} from '$/GraphQL/schema/types';

import { GlobeIcon, KeyIcon } from '$/images/FontAwesomeIcons';

import { useMe } from '$/hooks/useMe';

import { logger } from '$/utils/logger';
import EDate from '$/shared/frontend/utils/EDate';
import { hasPermission } from '$/utils/hasPermission';
import { rolesPermissionsMatrix } from '$/utils/pathConfig';

import {
  AddOnProvider,
  UpdateAddOnType,
  WithAddOnProps,
  useAddOnsContext,
} from './AddOns/AddOnsContext';
import { Per } from './AddOns/Components';
import { ProductInfo } from './ProductInfo';
import { usePlanPageContext } from './PlanPage';
import { getCardComponent } from './AddOns/CardComponent';
import { getListComponent } from './AddOns/ListComponent';
import { UpdateAddOnModal } from './AddOns/UpdateAddOnModal';
import { getListComponent as getAdditionalRegistrantsListComponent } from './AddOns/AdditionalRegistrantsListComponent';
import { AllAddOnsContext } from './AddOns/AllAddOnsContext';
import Text from '$/components/ui/Text';
import Form from '$/components/ui/Form';
import Settings from '$/components/ui/Settings';

export class AddOn extends ProductInfo {
  public get description(): string {
    return '';
  }

  public get currentPriceTier(): PriceTierFragment | undefined {
    return this.priceTiers[0];
  }

  public get shortDescription(): string {
    return '';
  }

  public get disclosure(): React.ReactElement | null {
    return null;
  }

  public getFreeUntil(me: MeFragment): EDate | undefined {
    let freeUntil: EDate | undefined = undefined;
    if (
      [SubscriptionSubstatus.Trialing, SubscriptionSubstatus.TrialingCanceled].includes(
        me.team.subscriptionSubstatus
      ) &&
      me.team.trialEndingDate
    ) {
      freeUntil = new EDate(me.team.trialEndingDate, 'UTC');
    }
    return freeUntil && freeUntil.getTime() > Date.now() ? freeUntil : undefined;
  }

  public getModalTitle(type: UpdateAddOnType): string {
    switch (type) {
      case UpdateAddOnType.Subscribe:
        return `Subscribe to add-on?`;
      case UpdateAddOnType.Resubscribe:
        return `Resubscribe to add-on?`;
      case UpdateAddOnType.Unsubscribe:
        return `Unsubscribe from add-on?`;
    }
  }

  public getModalMessage(
    type: UpdateAddOnType,
    billingCycle: BillingCycle,
    freeUntil?: EDate
  ): React.ReactElement {
    const formattedPrice = this.getFormattedPrice(billingCycle);
    const billingCycleText = billingCycle === BillingCycle.Month ? 'monthly' : 'annual';
    switch (type) {
      case UpdateAddOnType.Subscribe:
        return (
          <Text.body>
            {freeUntil ? (
              <p>
                If you subscribe to the <b>{this.name} add-on</b>, your {billingCycleText}{' '}
                subscription rate will not increase right away. Instead, the add-on will be free for
                you until <b>{freeUntil.format('P')}</b>, at which time you will be charged{' '}
                <b>
                  {formattedPrice}
                  <Per billingCycle={billingCycle} />
                </b>
                .
              </p>
            ) : (
              <p>
                If you subscribe to the <b>{this.name} add-on</b>, your {billingCycleText}{' '}
                subscription rate will increase by{' '}
                <b>
                  {formattedPrice}
                  <Per billingCycle={billingCycle} />
                </b>{' '}
                and you will be charged immediately at a prorated rate for the remainder of this
                billing cycle.
              </p>
            )}

            <p className='font-semibold'>
              Once charged, add-ons are non-refundable for the billing cycle in question. You may
              unsubscribe from them at any time.
            </p>

            {this.disclosure}

            <p>Do you wish to continue?</p>
          </Text.body>
        );
      case UpdateAddOnType.Resubscribe:
        return <div>Update ${this.name} Add-on</div>;
      case UpdateAddOnType.Unsubscribe:
        return (
          <Text.body>
            <p>
              If you unsubscribe from the <b>{this.name} add-on</b>, your subscription to it will
              remain active until the end of this billing cycle. If you subscribe again before that
              time, your access to this feature will remain uninterrupted.
            </p>

            {freeUntil ? (
              <p>
                Please note if you wish to remain subscribed to this add-on, it will remain free for
                you until <b>{freeUntil.format('P')}</b>, at which time you will be charged{' '}
                <b>
                  {formattedPrice}
                  <Per billingCycle={billingCycle} />
                </b>
                .
              </p>
            ) : null}

            <p>Do you wish to continue?</p>
          </Text.body>
        );
    }
  }

  public getCtaButtonText(type: UpdateAddOnType): string {
    switch (type) {
      case UpdateAddOnType.Subscribe:
        return `Subscribe to add-on`;
      case UpdateAddOnType.Resubscribe:
        return `Resubscribe to add-on`;
      case UpdateAddOnType.Unsubscribe:
        return `Unsubscribe from add-on`;
    }
  }

  public get CardComponent(): React.FC {
    return getCardComponent(this);
  }

  public get ListComponent(): React.FC {
    return getListComponent(this);
  }

  public get Icon(): React.FC<{ className?: string }> {
    return KeyIcon;
  }

  public image(): string | null {
    return null;
  }

  public get WithAddOnComponent(): React.FC<WithAddOnProps> {
    const WithAddOn: React.FC<WithAddOnProps> = ({ children, onAddSuccess }) => {
      const me = useMe();
      const { openModal, isModalOpen } = useAddOnsContext();
      const { showPermissionModal } = usePermissionModal();

      const onAdd = React.useCallback(() => {
        if (!this.priceTier) {
          logger.error('Missing priceTier in add-on ', this.name);
          return;
        }
        if (!hasPermission(rolesPermissionsMatrix.profilePlan.path, me)) {
          showPermissionModal?.(rolesPermissionsMatrix.profilePlan.path);
          return;
        }
        openModal({
          addOn: this,
          tierId: this.priceTier.tierId,
          type: UpdateAddOnType.Subscribe,
          onAddSuccess,
        });
      }, [me, onAddSuccess, openModal, showPermissionModal]);

      const isAvailable = this.isSubscribed;

      const ManageLink = React.useMemo(() => {
        return (
          <>
            {isAvailable && (
              <Form.Link to='/home/profile/plan' className='whitespace-nowrap text-base font-body'>
                Manage subscription add-ons
              </Form.Link>
            )}
          </>
        );
      }, [isAvailable]);

      return React.useMemo(
        () => (
          <>
            {children({ isAvailable, onAdd, isModalOpen, ManageLink })}
            <UpdateAddOnModal />
          </>
        ),
        [children, isAvailable, onAdd, ManageLink, isModalOpen]
      );
    };
    const WithAddOnComponent: React.FC<WithAddOnProps> = (props) => {
      return (
        <AddOnProvider>
          <WithAddOn {...props} />
        </AddOnProvider>
      );
    };
    return WithAddOnComponent;
  }
}

export class CustomDomains extends AddOn {
  public get shortDescription(): string {
    return 'Host your webinars and landing pages on your website or on any domain you own, thus making it possible to track people more effectively by keeping them on the same domain as they register for and attend your webinars.';
  }

  public get description(): string {
    return `Host your webinars on your own website, which makes it possible to track registrants more effectively by keeping them on the same domain.`;
  }

  public get Icon(): React.FC {
    return GlobeIcon;
  }

  public image(): string | null {
    return require('$/images/CustomDomain.jpg').default;
  }
}

export class AccessControl extends AddOn {
  public get shortDescription(): string {
    return 'Advanced access control includes whitelisting/blacklisting by email and/or domain, requiring work emails, and blocking the use of temporary emails.';
  }

  public get description(): string {
    return 'Limit who (and how many) can access your webinars, like only those who are using a work email address or an email on a specified domain.';
  }

  public image(): string | null {
    return require('$/images/AccessControl.jpg').default;
  }
}

export class RemoveBranding extends AddOn {
  public get shortDescription(): string {
    return 'Remove eWebinar branding across the product.';
  }

  public get description(): string {
    return 'Remove ‘Powered by eWebinar’ from where it appears on your registration pages, in your notification emails, and in the webinar room.';
  }

  public image(): string | null {
    return require('$/images/RemoveBranding.png').default;
  }
}

export class Salesforce extends AddOn {
  public get shortDescription(): string {
    return 'Create and update leads or contacts in Salesforce with interaction responses, behavioral data, and more when people sign up for and attend your webinars.';
  }

  public get description(): string {
    return 'Create and update leads or contacts  in Salesforce with interaction responses, behavioral data, and more when people sign up for and attend your webinars.';
  }

  public get disclosure(): React.ReactElement | null {
    return (
      <p>
        <b>NOTE</b>: When you use eWebinar’s integration with Salesforce, your registration
        information will be disclosed to SFDC pursuant to its privacy policy available at{' '}
        <a target='_blank' href='https://salesforce.com'>
          salesforce.com
        </a>
        .
      </p>
    );
  }

  public image(): string | null {
    return require('$/images/logos/big-salesforce.png').default;
  }
}

export class Marketo extends AddOn {
  public get shortDescription(): string {
    return 'Create and update contacts in Marketo with interaction responses, behavioral data, and more when people sign up for and attend your webinars.';
  }

  public get description(): string {
    return 'Create and update contacts in Marketo with interaction responses, behavioral data, and more when people sign up for and attend your webinars.';
  }

  public image(): string | null {
    return require('$/images/logos/big-marketo.png').default;
  }
}

export class AdditionalRegistrants extends AddOn {
  public get type(): ProductType {
    return ProductType.AdditionalRegistrants;
  }

  public get isUnsubscribed(): boolean {
    return false;
  }

  public get scheduledQuantity(): number {
    return (this.product as ProductForAdminFragment).scheduledQuantity ?? this.quantity;
  }

  public calculatePrice(billingCycle: BillingCycle, quantity: number): number {
    const noOfPackage = Math.ceil(quantity / 1000);
    switch (billingCycle) {
      case BillingCycle.Month:
        return (this.priceTier?.monthlyPrice ?? 0) * noOfPackage;
      case BillingCycle.Year:
        return (this.priceTier?.annualPrice ?? 0) * noOfPackage;
    }
  }

  public get noOfPackage(): number {
    return Math.ceil(this.quantity / 1000);
  }

  public get nextNoOfPackage(): number {
    return Math.ceil(this.scheduledQuantity / 1000);
  }

  public get prices(): {
    monthly: number;
    annual: number;
    annualByMonth: number;
  } {
    return {
      monthly: (this.priceTier?.monthlyPrice ?? 0) * this.noOfPackage,
      annual: (this.priceTier?.annualPrice ?? 0) * this.noOfPackage,
      annualByMonth: (this.priceTier?.annualPricePerMonth ?? 0) * this.noOfPackage,
    };
  }

  public get nextPrices(): {
    monthly: number;
    annual: number;
    annualByMonth: number;
  } {
    return {
      monthly: (this.nextPriceTier?.monthlyPrice ?? 0) * this.nextNoOfPackage,
      annual: (this.nextPriceTier?.annualPrice ?? 0) * this.nextNoOfPackage,
      annualByMonth: (this.nextPriceTier?.annualPricePerMonth ?? 0) * this.nextNoOfPackage,
    };
  }

  public get ListComponent(): React.FC {
    return getAdditionalRegistrantsListComponent(this);
  }
}

export const getAddOn = (product: ProductFragment): AddOn => {
  switch (product.type) {
    case ProductType.AccessControl:
      return new AccessControl(product);
    case ProductType.Domains:
      return new CustomDomains(product);
    case ProductType.Salesforce:
      return new Salesforce(product);
    case ProductType.Marketo:
      return new Marketo(product);
    case ProductType.AdditionalRegistrants:
      return new AdditionalRegistrants(product);
    case ProductType.RemoveBranding:
      return new RemoveBranding(product);
    default:
      return new AddOn(product);
  }
};

export const AllAddOnsContextProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const addOnsQuery = useAddOnsQuery();
  const addOns = React.useMemo(() => {
    return addOnsQuery.data?.addOns.map(getAddOn);
  }, [addOnsQuery.data?.addOns]);
  if (!addOns) return <Loading />;
  return <AllAddOnsContext.Provider value={{ addOns }}>{children}</AllAddOnsContext.Provider>;
};

export const AvailableAddOns: React.FC = () => {
  const { availableAddOns } = usePlanPageContext();

  if (availableAddOns.length === 0) return null;

  return (
    <Settings.Section id='addons'>
      <Settings.Subsection>
        <Settings.Title>Available add-ons</Settings.Title>
        <Settings.Description>
          Add-ons increase the power of your eWebinar subscription to meet the specific needs of
          your business.{' '}
          <a target='blank' href='https://ewebinar.com/help/subscription-add-ons'>
            Learn more
          </a>
        </Settings.Description>
        {availableAddOns.length === 0 && (
          <Settings.Description className='text-center'>
            No more available add-on
          </Settings.Description>
        )}
        <div
          className='flex flex-row flex-wrap mt-3'
          css={css`
            margin-left: -0.5rem;
            margin-right: -0.5rem;
          `}
        >
          {availableAddOns.map((AddOn) => (
            <AddOn.CardComponent key={AddOn.type} />
          ))}
        </div>
      </Settings.Subsection>
    </Settings.Section>
  );
};
